import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import BookUploadModal from '../components/BookUploadModal';
import { canUploadBooksHere } from '../utils/uploadPermissions';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../config/api';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  BuildingLibraryIcon,
  DocumentArrowUpIcon,
  BookOpenIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

interface BookRequest {
  id: string;
  title: string;
  author: string;
  status: string;
  uploaded_at: string;
  rejection_reason?: string;
  linked_to_book_id?: string;
  linked_book_title?: string;
  cover_presigned_url?: string;
  total_pages?: number;
  // Set when the book was uploaded from a tenant subdomain by a member
  // of that institute. UI surfaces a chip + reminds the uploader that
  // once approved, the book lands in the institute library, not their
  // personal one.
  institution_id?: string;
  institution_name?: string;
  institution_slug?: string;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  pending: { label: 'Under review', color: 'text-amber-800', bgColor: 'bg-amber-100' },
  in_review: { label: 'Under review', color: 'text-amber-800', bgColor: 'bg-amber-100' },
  approved: { label: 'Available', color: 'text-green-800', bgColor: 'bg-green-100' },
  rejected: { label: 'Rejected', color: 'text-red-800', bgColor: 'bg-red-100' },
};

// Format a timestamp as a human-friendly relative-time string like "3 days ago"
// or "2 hours ago". Falls back to absolute date for anything older than ~30 days.
//
// Backend serializes Book.uploaded_at as a naive ISO datetime (no Z, no
// offset). The DB column is `timestamp without time zone` and the value
// is naive UTC (datetime.utcnow), but JS new Date() parses no-TZ strings
// as *local* time per ISO 8601 spec. For users in negative-offset TZs
// (or any TZ where local interpretation pushes the parsed time past the
// real now), the diff goes negative and Math.max(0, ...) yields "just
// now" forever. Append a 'Z' marker to force UTC parsing.
const formatRelativeTime = (dateStr: string): string => {
  const now = Date.now();
  const normalized = /[zZ]|[+-]\d{2}:\d{2}$/.test(dateStr) ? dateStr : `${dateStr}Z`;
  const then = new Date(normalized).getTime();
  const seconds = Math.max(0, Math.floor((now - then) / 1000));

  if (seconds < 60) return 'just now';
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    return `${m} minute${m === 1 ? '' : 's'} ago`;
  }
  if (seconds < 86400) {
    const h = Math.floor(seconds / 3600);
    return `${h} hour${h === 1 ? '' : 's'} ago`;
  }
  const days = Math.floor(seconds / 86400);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const MyBookRequests: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<BookRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const canUpload = canUploadBooksHere(user);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/books/my-requests');
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch book requests:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-8">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              My Book Requests
            </h1>
            <p className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>
              Track the status of books you've uploaded for review
            </p>
          </div>
          {!loading && requests.length > 0 && canUpload && (
            <button
              onClick={() => setShowUploadModal(true)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                isDarkMode
                  ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              <ArrowUpTrayIcon className="h-4 w-4" />
              Upload another
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className={`h-8 w-8 animate-spin ${isDarkMode ? 'text-cyan-400' : 'text-cyan-500'}`} />
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-16">
            <DocumentArrowUpIcon className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              No book requests yet
            </h3>
            <p className={`mb-6 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
              {canUpload
                ? 'Upload a book and track its review status here.'
                : 'You don\'t have any pending book requests.'}
            </p>
            {canUpload && (
              <button
                onClick={() => setShowUploadModal(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white whitespace-nowrap"
                style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
              >
                <ArrowUpTrayIcon className="h-4 w-4" />
                Upload a book
              </button>
            )}
          </div>
        ) : (
          <div className={`rounded-xl overflow-hidden border ${
            isDarkMode ? 'border-slate-700' : 'border-gray-200'
          }`}>
            <table className="w-full">
              <thead>
                <tr className={isDarkMode ? 'bg-slate-800' : 'bg-gray-50'}>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? 'text-slate-400' : 'text-gray-500'
                  }`}>Book</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? 'text-slate-400' : 'text-gray-500'
                  }`}>Submitted</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? 'text-slate-400' : 'text-gray-500'
                  }`}>Status</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? 'text-slate-400' : 'text-gray-500'
                  }`}>Details</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDarkMode ? 'text-slate-400' : 'text-gray-500'
                  }`}></th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-200'}`}>
                {requests.map((req) => {
                  const statusConfig = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
                  // Non-admin uploads carry empty title/author at submit time —
                  // admins fill them in during review. Show a friendlier placeholder
                  // until that happens.
                  const displayTitle = req.title?.trim() || 'Uploaded file (awaiting title)';
                  const displayAuthor = req.author?.trim() || '—';
                  return (
                    <tr key={req.id} className={isDarkMode ? 'bg-slate-900/50' : 'bg-white'}>
                      <td className="px-6 py-4">
                        <div className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {displayTitle}
                        </div>
                        <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                          {displayAuthor}
                        </div>
                      </td>
                      <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                        <span title={new Date(req.uploaded_at).toLocaleString()}>
                          {formatRelativeTime(req.uploaded_at)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusConfig.bgColor} ${statusConfig.color}`}>
                            {statusConfig.label}
                          </span>
                          {/* Institute attribution chip — uploads tagged to
                              an institute land in that institute's library
                              once approved, not the uploader's personal one.
                              Surfacing this here heads off the "I uploaded
                              a book, why isn't it in my library?" confusion. */}
                          {req.institution_name && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                              isDarkMode ? 'bg-cyan-500/15 text-cyan-300' : 'bg-cyan-50 text-cyan-700'
                            }`}>
                              <BuildingLibraryIcon className="h-3 w-3" />
                              {req.institution_name}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        {req.rejection_reason && (
                          <span className="text-red-600">{req.rejection_reason}</span>
                        )}
                        {/* Linked-child match is an internal de-dup detail.
                            Once approved, the user just reads the canonical
                            book — they don't need to know about the merge.
                            We surface this only for non-approved states (e.g.
                            in case future statuses want it back). */}
                        {req.institution_name && req.status !== 'rejected' && (
                          <span className={`inline-flex items-start gap-1 mt-1 ${
                            isDarkMode ? 'text-slate-400' : 'text-gray-500'
                          }`}>
                            <InformationCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-cyan-500" />
                            <span>
                              Once approved, this book will be available to
                              {' '}<strong>{req.institution_name}</strong>{' '}
                              members via the institute library.
                            </span>
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {req.status === 'approved' && (
                          <button
                            onClick={() => {
                              const bookId = req.linked_to_book_id || req.id;
                              navigate(`/dhyana/${bookId}`);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-white whitespace-nowrap"
                            style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
                          >
                            <BookOpenIcon className="h-4 w-4" />
                            Read
                          </button>
                        )}
                        {req.status === 'rejected' && canUpload && (
                          <button
                            onClick={() => setShowUploadModal(true)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg whitespace-nowrap ${
                              isDarkMode
                                ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                            }`}
                          >
                            <ArrowUpTrayIcon className="h-4 w-4" />
                            Re-upload
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <BookUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={() => {
          setShowUploadModal(false);
          fetchRequests();
        }}
        isAdmin={user?.is_admin}
      />
    </Layout>
  );
};

export default MyBookRequests;
